import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../api/client";
import { CLAUDE_CODE_PRESETS, ModelPicker } from "./ModelPicker";
import type {
  AdminJob,
  ValidatorEvalEstimate,
  ValidatorEvalItem,
  ValidatorEvalRunDetail,
  ValidatorEvalRunSummary,
} from "../../api/client";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";

// Validator Test admin tool.
//
// Pick a model, click Run. The Phoenix job invokes
// `tools/validator_eval.py` which sends the committed training-set
// few-shot examples plus each validation item to the model. We score
// the model's verdicts against the ground truth and render the result
// here. Repeated calls within a run share a byte-identical prefix so
// prompt caching kicks in on the second-and-later items.

const DEFAULT_MODEL = "claude-haiku-4-5-20251001";

interface FormState {
  model: string;
  thinking: boolean;
}

const DEFAULT_FORM: FormState = {
  model: DEFAULT_MODEL,
  thinking: false,
};

export function ValidatorTestTool() {
  const theme = pixelTheme;

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<ValidatorEvalRunSummary[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<ValidatorEvalRunDetail | null>(
    null,
  );
  const [activeRunError, setActiveRunError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [estimate, setEstimate] = useState<ValidatorEvalEstimate | null>(null);

  const refreshRuns = useCallback(async () => {
    try {
      const { runs } = await api.listValidatorEvalRuns();
      setRuns(runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const refreshJobs = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      setJobs(jobs.filter((j) => j.kind === "validator_eval"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
    void refreshJobs();
    const id = setInterval(() => {
      void refreshRuns();
      void refreshJobs();
    }, 2000);
    return () => clearInterval(id);
  }, [refreshRuns, refreshJobs]);

  // Refresh the cost preview whenever model OR the thinking flag
  // changes. Both axes affect the server's answer: thinking on
  // requires a measured probe (or `requires_sample: true` is
  // returned), thinking off uses the static formula. Debounce
  // through a short timer so a flurry of typing in the OpenRouter
  // picker doesn't fire one request per keystroke.
  useEffect(() => {
    const trimmed = form.model.trim();
    if (!trimmed) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const e = await api.getValidatorEvalEstimate(trimmed, {
          thinking: form.thinking,
        });
        if (!cancelled) setEstimate(e);
      } catch {
        if (!cancelled) setEstimate(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [form.model, form.thinking]);

  const [sampling, setSampling] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);

  // Trigger one sample probe and re-fetch the estimate when the job
  // lands. The job runs in the bounded-parallelism queue alongside
  // any in-flight validator_eval jobs (it shares the same kill +
  // resume plumbing), so the user can cancel mid-probe if they
  // change their mind.
  const onSampleBudget = useCallback(async () => {
    const trimmed = form.model.trim();
    if (!trimmed) return;
    setSampling(true);
    setSampleError(null);
    try {
      const { job_id } = await api.sampleValidatorEvalCall({
        model: trimmed,
        thinking: form.thinking,
      });
      // Poll the jobs list until the sample finishes. The list is
      // already refreshed every 2 s by the outer interval; we
      // shortcut here so the user doesn't wait two seconds for a
      // ~1 s job to bubble through.
      const start = Date.now();
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 1500));
        const { jobs } = await api.listAdminJobs();
        const j = jobs.find((x) => x.id === job_id);
        if (!j) break;
        if (j.status === "succeeded") {
          break;
        }
        if (j.status === "failed") {
          setSampleError(
            (j.error && JSON.stringify(j.error)) || "sample call failed",
          );
          break;
        }
      }
      // Refresh estimate either way — succeeded means there's a new
      // sample on disk; failed means the user should see the old
      // requires_sample state with our error text below.
      const e = await api.getValidatorEvalEstimate(trimmed, {
        thinking: form.thinking,
      });
      setEstimate(e);
    } catch (err) {
      setSampleError(err instanceof Error ? err.message : String(err));
    } finally {
      setSampling(false);
    }
  }, [form.model, form.thinking]);

  useEffect(() => {
    if (!activeRunId) {
      setActiveRun(null);
      setActiveRunError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.getValidatorEvalRun(activeRunId);
        if (!cancelled) {
          setActiveRun(detail);
          setActiveRunError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setActiveRunError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeRunId, runs]);

  async function onRun() {
    setError(null);
    if (!form.model.trim()) {
      setError("Pick a model.");
      return;
    }
    setBusy(true);
    try {
      await api.enqueueValidatorEval({
        model: form.model.trim(),
        thinking: form.thinking,
      });
      await refreshJobs();
      await refreshRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Merge the runs list with the in-flight validator_eval jobs so a
  // job that's started but hasn't yet written its `progress.json`
  // (the brief setup window before the first item) still shows up.
  // Jobs whose `args.run_id` already maps to a real on-disk run are
  // omitted — we'd be double-counting the same work. The remainder
  // (jobs that haven't yet stamped a progress.json) appear as
  // synthetic "starting…" rows keyed by `job:<id>`.
  const mergedRuns = useMemo(() => {
    const runningJobs = jobs.filter(
      (j) => j.status === "queued" || j.status === "running",
    );
    const knownRunIds = new Set(runs.map((r) => r.run_id));
    const phantoms: ValidatorEvalRunSummary[] = runningJobs
      .filter((j) => {
        const argRunId = (j.args as Record<string, unknown>)?.["run_id"] as
          | string
          | undefined;
        return !argRunId || !knownRunIds.has(argRunId);
      })
      .map((j) => ({
        run_id: `job:${j.id}`,
        created_at: j.inserted_at,
        kind: "validator_eval" as const,
        status: "running" as const,
        progress: { current: 0, total: 0, phase: "starting" as const },
        model:
          ((j.args as Record<string, unknown>)?.["model"] as string) ?? null,
        thinking:
          ((j.args as Record<string, unknown>)?.["thinking"] as boolean) ??
          false,
        scores: null,
        item_count: 0,
        dataset_size: 0,
        half_a_size: 0,
        half_b_size: 0,
        resumable: false,
      }));
    return [...phantoms, ...runs];
  }, [runs, jobs]);

  // Match a run to its job by `run_id` so cancel always lands on the
  // right subprocess. Synthetic "job:<id>" rows carry the job id in
  // their run_id directly. Real runs match through `args.run_id`.
  function findJobForRun(run: ValidatorEvalRunSummary): string | null {
    if (run.run_id.startsWith("job:")) return run.run_id.slice(4);
    const match = jobs.find((j) => {
      if (j.status !== "running" && j.status !== "queued") return false;
      const argRunId = (j.args as Record<string, unknown>)?.["run_id"];
      return argRunId === run.run_id;
    });
    return match?.id ?? null;
  }

  async function onCancel(run: ValidatorEvalRunSummary) {
    const target = findJobForRun(run);
    if (!target) {
      setError("No running job to cancel for this run.");
      return;
    }
    try {
      await api.cancelAdminJob(target);
      await refreshJobs();
      await refreshRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onResume(run: ValidatorEvalRunSummary) {
    setError(null);
    try {
      await api.resumeValidatorEvalRun(run.run_id);
      await refreshJobs();
      await refreshRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onDelete(run: ValidatorEvalRunSummary) {
    setError(null);
    if (run.run_id.startsWith("job:")) {
      setError("Cancel the starting job before deleting.");
      return;
    }
    try {
      await api.deleteValidatorEvalRun(run.run_id);
      if (activeRunId === run.run_id) setActiveRunId(null);
      await refreshRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 12,
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SpecForm
          theme={theme}
          form={form}
          setForm={setForm}
          onRun={onRun}
          busy={busy}
          estimate={estimate}
          onSampleBudget={onSampleBudget}
          sampling={sampling}
          sampleError={sampleError}
        />
        {error && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${theme.accent}`,
              color: theme.accent,
              background: theme.panel,
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 360px) 1fr",
          gap: 12,
          minHeight: 0,
        }}
      >
        <RunsList
          runs={mergedRuns}
          activeRunId={activeRunId}
          onSelect={setActiveRunId}
          onCancel={onCancel}
          onResume={onResume}
          onDelete={onDelete}
          theme={theme}
        />
        <RunViewer
          run={activeRun}
          activeSummary={
            mergedRuns.find((r) => r.run_id === activeRunId) ?? null
          }
          error={activeRunError}
          loading={Boolean(activeRunId) && !activeRun && !activeRunError}
          theme={theme}
        />
      </div>
    </div>
  );
}

function SpecForm({
  theme,
  form,
  setForm,
  onRun,
  busy,
  estimate,
  onSampleBudget,
  sampling,
  sampleError,
}: {
  theme: typeof pixelTheme;
  form: FormState;
  setForm: (next: FormState) => void;
  onRun: () => void;
  busy: boolean;
  estimate: ValidatorEvalEstimate | null;
  onSampleBudget: () => void;
  sampling: boolean;
  sampleError: string | null;
}) {
  const requiresSample = estimate?.requires_sample === true;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "center",
        padding: 12,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
      }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: theme.inkDim,
            letterSpacing: theme.letterSpacing,
          }}
        >
          MODEL UNDER TEST
        </span>
        <ModelPicker
          value={form.model}
          onChange={(next) => setForm({ ...form, model: next })}
          presets={CLAUDE_CODE_PRESETS}
          disabled={busy}
        />
      </label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: theme.ink,
          fontSize: 12,
        }}
      >
        <input
          type="checkbox"
          checked={form.thinking}
          onChange={(e) => setForm({ ...form, thinking: e.target.checked })}
          disabled={busy}
        />
        Extended thinking
      </label>
      <CostPreview estimate={estimate} theme={theme} />
      {requiresSample && (
        <button
          onClick={onSampleBudget}
          disabled={sampling || busy}
          style={{
            padding: "6px 12px",
            background: "transparent",
            color: theme.accent,
            border: `2px solid ${theme.accent}`,
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            cursor: sampling || busy ? "not-allowed" : "pointer",
          }}
          title="Make one real call so the cost estimator can measure how many reasoning tokens this model spends per item"
        >
          {sampling ? "SAMPLING…" : "SAMPLE BUDGET"}
        </button>
      )}
      {sampleError && (
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            color: theme.accent,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {sampleError}
        </span>
      )}
      <button
        onClick={onRun}
        disabled={busy}
        style={{
          marginLeft: "auto",
          padding: "8px 16px",
          background: busy ? theme.panel : theme.accent,
          color: busy ? theme.inkFaint : E32.parchment,
          border: `2px solid ${theme.accent}`,
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          letterSpacing: theme.letterSpacing,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "QUEUEING…" : "RUN"}
      </button>
    </div>
  );
}

function CostPreview({
  estimate,
  theme,
}: {
  estimate: ValidatorEvalEstimate | null;
  theme: typeof pixelTheme;
}) {
  if (!estimate) {
    return (
      <div style={{ fontSize: 11, color: theme.inkFaint }}>
        Loading cost estimate…
      </div>
    );
  }

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 9,
    color: theme.inkDim,
    letterSpacing: theme.letterSpacing,
  };
  const valueStyle: React.CSSProperties = {
    color: theme.ink,
    fontSize: 13,
    fontVariantNumeric: "tabular-nums",
  };

  if (estimate.requires_sample) {
    // Extended thinking is on but we haven't measured the model's
    // per-call reasoning-token usage yet. Refusing to display a
    // dollar amount is the honest path — the prior behavior of
    // showing the no-thinking estimate was misleading.
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={labelStyle}>EST. COST</span>
        <span style={{ ...valueStyle, color: theme.inkFaint }}>
          requires probe — click SAMPLE BUDGET
        </span>
      </div>
    );
  }

  if (estimate.cost_usd == null) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={labelStyle}>EST. COST</span>
        <span style={{ ...valueStyle, color: theme.inkFaint }}>
          unknown — model not in rate table
        </span>
      </div>
    );
  }

  const cachingNote = estimate.supports_caching
    ? "with prompt caching"
    : "no prompt caching";
  const sampleNote = estimate.thinking
    ? `, ${estimate.output_tokens_per_call ?? "?"} out tokens/call (sampled)`
    : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={labelStyle}>EST. COST</span>
      <span style={valueStyle}>
        {fmtUsd(estimate.cost_usd)}{" "}
        <span style={{ color: theme.inkFaint, fontSize: 11 }}>
          ({estimate.validation_count} calls, {cachingNote}
          {sampleNote})
        </span>
      </span>
    </div>
  );
}

function fmtUsd(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function RunsList({
  runs,
  activeRunId,
  onSelect,
  onCancel,
  onResume,
  onDelete,
  theme,
}: {
  runs: ValidatorEvalRunSummary[];
  activeRunId: string | null;
  onSelect: (id: string | null) => void;
  onCancel: (run: ValidatorEvalRunSummary) => void;
  onResume: (run: ValidatorEvalRunSummary) => void;
  onDelete: (run: ValidatorEvalRunSummary) => void;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: theme.inkDim,
          letterSpacing: theme.letterSpacing,
        }}
      >
        RUNS
      </div>
      {runs.length === 0 ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>No runs yet.</div>
      ) : (
        runs.map((r) => (
          <RunRow
            key={r.run_id}
            run={r}
            active={r.run_id === activeRunId}
            onSelect={onSelect}
            onCancel={onCancel}
            onResume={onResume}
            onDelete={onDelete}
            theme={theme}
          />
        ))
      )}
    </div>
  );
}

function RunRow({
  run,
  active,
  onSelect,
  onCancel,
  onResume,
  onDelete,
  theme,
}: {
  run: ValidatorEvalRunSummary;
  active: boolean;
  onSelect: (id: string | null) => void;
  onCancel: (run: ValidatorEvalRunSummary) => void;
  onResume: (run: ValidatorEvalRunSummary) => void;
  onDelete: (run: ValidatorEvalRunSummary) => void;
  theme: typeof pixelTheme;
}) {
  const isRunning = run.status === "running";
  const isPaused = run.status === "paused";
  const fg = active ? E32.parchment : theme.ink;
  const subFg = active ? E32.parchment : theme.inkFaint;
  return (
    <div
      style={{
        padding: 8,
        background: active ? theme.accent : theme.panel,
        color: fg,
        border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
        display: "grid",
        gap: 6,
      }}
    >
      <button
        onClick={() => onSelect(active ? null : run.run_id)}
        style={{
          background: "transparent",
          border: "none",
          color: "inherit",
          textAlign: "left",
          padding: 0,
          cursor: "pointer",
          display: "grid",
          gap: 4,
        }}
      >
        <div style={{ fontFamily: "monospace", fontSize: 12 }}>
          {run.run_id.startsWith("job:") ? "(starting…)" : run.run_id}
          {isPaused ? " · PAUSED" : ""}
        </div>
        <div style={{ fontSize: 11, color: subFg }}>
          {run.model || "(unknown model)"}
          {run.thinking ? " · thinking" : ""}
        </div>
        {isRunning || isPaused ? (
          <ProgressMini run={run} theme={theme} active={active} />
        ) : run.scores ? (
          <div style={{ fontSize: 11, color: subFg }}>
            {fmtPct(run.scores.overall.pct)} ·{" "}
            {run.scores.overall.correct}/{run.scores.overall.total} ·{" "}
            {fmtCreatedAt(run.created_at)}
          </div>
        ) : null}
      </button>
      {(isRunning || isPaused) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {isRunning && (
            <RowActionButton
              label="CANCEL"
              onClick={() => onCancel(run)}
              active={active}
              theme={theme}
            />
          )}
          {isPaused && run.resumable && (
            <RowActionButton
              label="RESUME"
              onClick={() => onResume(run)}
              active={active}
              theme={theme}
            />
          )}
          {isPaused && (
            <RowActionButton
              label="DELETE"
              onClick={() => onDelete(run)}
              active={active}
              theme={theme}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RowActionButton({
  label,
  onClick,
  active,
  theme,
}: {
  label: string;
  onClick: () => void;
  active: boolean;
  theme: typeof pixelTheme;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        padding: "2px 8px",
        background: "transparent",
        color: active ? E32.parchment : theme.accent,
        border: `1px solid ${active ? E32.parchment : theme.accent}`,
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        letterSpacing: theme.letterSpacing,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function ProgressMini({
  run,
  theme,
  active,
}: {
  run: ValidatorEvalRunSummary;
  theme: typeof pixelTheme;
  active: boolean;
}) {
  const p = run.progress;
  const total = p?.total ?? 0;
  const current = p?.current ?? 0;
  const ratio = total > 0 ? Math.min(1, current / total) : 0;
  const subFg = active ? E32.parchment : theme.inkFaint;
  const barBg = active ? "rgba(255,255,255,0.25)" : theme.bgDeep;
  const barFg = active ? E32.parchment : theme.accent;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, color: subFg }}>
        {p?.phase === "starting" || total === 0
          ? "starting…"
          : `${current} / ${total} (${fmtPct(ratio)})`}
      </div>
      <div
        style={{
          height: 6,
          background: barBg,
          border: `1px solid ${active ? E32.parchment : theme.panelEdge}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${ratio * 100}%`,
            background: barFg,
            transition: "width 200ms linear",
          }}
        />
      </div>
    </div>
  );
}

function fmtPct(p: number): string {
  return `${Math.round(p * 1000) / 10}%`;
}

function fmtCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function RunViewer({
  run,
  activeSummary,
  error,
  loading,
  theme,
}: {
  run: ValidatorEvalRunDetail | null;
  activeSummary: ValidatorEvalRunSummary | null;
  error: string | null;
  loading: boolean;
  theme: typeof pixelTheme;
}) {
  // For a still-running run there's no detail to load yet; show the
  // progress + waiting message based on the summary instead.
  if (activeSummary && activeSummary.status === "running") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: 12,
          color: theme.ink,
          fontSize: 13,
        }}
      >
        <div style={{ fontFamily: "monospace", fontSize: 14 }}>
          {activeSummary.run_id.startsWith("job:")
            ? "(starting…)"
            : activeSummary.run_id}
        </div>
        <div style={{ color: theme.inkFaint }}>
          {activeSummary.model || "(unknown model)"}
          {activeSummary.thinking ? " · thinking" : ""}
        </div>
        <div style={{ maxWidth: 480 }}>
          <ProgressMini run={activeSummary} theme={theme} active={false} />
        </div>
        <div style={{ color: theme.inkFaint, fontSize: 12 }}>
          Per-item results will appear here once the run finishes.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ color: theme.inkFaint, padding: 12, fontSize: 13 }}>
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ color: theme.accent, padding: 12, fontSize: 13 }}>
        {error}
      </div>
    );
  }
  if (!run) {
    return (
      <div style={{ color: theme.inkFaint, padding: 12, fontSize: 13 }}>
        Pick a run from the list, or click Run to start one.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        overflowY: "auto",
        paddingRight: 4,
      }}
    >
      <ScoreHeader run={run} theme={theme} />
      <ItemsTable items={run.items} theme={theme} />
    </div>
  );
}

function ScoreHeader({
  run,
  theme,
}: {
  run: ValidatorEvalRunDetail;
  theme: typeof pixelTheme;
}) {
  const cell = (
    label: string,
    block: { correct: number; total: number; pct: number },
  ) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 140,
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: theme.inkDim,
          letterSpacing: theme.letterSpacing,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 24, color: theme.ink }}>{fmtPct(block.pct)}</div>
      <div style={{ fontSize: 11, color: theme.inkFaint }}>
        {block.correct}/{block.total}
      </div>
    </div>
  );

  if (!run.scores) return null;

  return (
    <div
      style={{
        padding: 12,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          fontSize: 12,
          color: theme.inkFaint,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontFamily: "monospace", color: theme.ink }}>
          {run.model}
        </span>
        {run.thinking && <span>· thinking</span>}
        <span>· {fmtCreatedAt(run.created_at)}</span>
        <span>
          · {run.dataset_size} items (2-fold cross-validation)
        </span>
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {cell("OVERALL", run.scores.overall)}
        {cell("VALID → VALID", run.scores.valid_recall)}
        {cell("INVALID → INVALID", run.scores.invalid_recall)}
      </div>
    </div>
  );
}

function ItemsTable({
  items,
  theme,
}: {
  items: ValidatorEvalItem[];
  theme: typeof pixelTheme;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 90px 90px 50px 70px",
          padding: "6px 8px",
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
        }}
      >
        <div>ID</div>
        <div>EXPECTED</div>
        <div>PREDICTED</div>
        <div>OK?</div>
        <div>LLM&nbsp;s</div>
      </div>
      {items.map((it) => (
        <ItemRow
          key={it.id}
          item={it}
          theme={theme}
          expanded={!!expanded[it.id]}
          onToggle={() =>
            setExpanded({ ...expanded, [it.id]: !expanded[it.id] })
          }
        />
      ))}
    </div>
  );
}

function ItemRow({
  item,
  theme,
  expanded,
  onToggle,
}: {
  item: ValidatorEvalItem;
  theme: typeof pixelTheme;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        border: `1px solid ${theme.panelEdge}`,
        background: theme.panel,
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 90px 90px 50px 70px",
          padding: "6px 8px",
          background: "transparent",
          border: "none",
          color: theme.ink,
          fontSize: 12,
          textAlign: "left",
          cursor: "pointer",
          alignItems: "center",
        }}
      >
        <div style={{ fontFamily: "monospace" }}>{item.id}</div>
        <div>
          <VerdictPill ok={item.expected.ok} theme={theme} />
        </div>
        <div>
          <VerdictPill ok={item.predicted.ok} theme={theme} />
        </div>
        <div
          style={{
            color: item.correct ? E32.fern : theme.accent,
            fontFamily: theme.fontDisplay,
            fontSize: 12,
          }}
        >
          {item.correct ? "✓" : "✗"}
        </div>
        <div style={{ color: theme.inkFaint, fontFamily: "monospace" }}>
          {item.timings.llm.toFixed(1)}
        </div>
      </button>
      {expanded && <ItemDetail item={item} theme={theme} />}
    </div>
  );
}

function VerdictPill({
  ok,
  theme,
}: {
  ok: boolean | null;
  theme: typeof pixelTheme;
}) {
  if (ok === true) {
    return (
      <span
        style={{
          padding: "2px 6px",
          border: `1px solid ${E32.fern}`,
          color: E32.fern,
          fontSize: 10,
          fontFamily: theme.fontDisplay,
          letterSpacing: theme.letterSpacing,
        }}
      >
        VALID
      </span>
    );
  }
  if (ok === false) {
    return (
      <span
        style={{
          padding: "2px 6px",
          border: `1px solid ${theme.accent}`,
          color: theme.accent,
          fontSize: 10,
          fontFamily: theme.fontDisplay,
          letterSpacing: theme.letterSpacing,
        }}
      >
        INVALID
      </span>
    );
  }
  return (
    <span
      style={{
        padding: "2px 6px",
        border: `1px solid ${theme.panelEdge}`,
        color: theme.inkFaint,
        fontSize: 10,
        fontFamily: theme.fontDisplay,
        letterSpacing: theme.letterSpacing,
      }}
    >
      ?
    </span>
  );
}

function ItemDetail({
  item,
  theme,
}: {
  item: ValidatorEvalItem;
  theme: typeof pixelTheme;
}) {
  const labelCss: React.CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 9,
    color: theme.inkDim,
    letterSpacing: theme.letterSpacing,
  };
  const preCss: React.CSSProperties = {
    margin: 0,
    padding: 8,
    border: `1px solid ${theme.panelEdge}`,
    background: theme.bgDeep,
    color: theme.ink,
    fontSize: 11,
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 300,
    overflowY: "auto",
  };
  const cardJsonText = useMemo(
    () => JSON.stringify(item.card_json, null, 2),
    [item.card_json],
  );
  return (
    <div
      style={{
        padding: 12,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        borderTop: `1px solid ${theme.panelEdge}`,
        background: theme.bg,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={labelCss}>EXPECTED</div>
        <pre style={preCss}>{JSON.stringify(item.expected, null, 2)}</pre>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={labelCss}>PREDICTED</div>
        <pre style={preCss}>{JSON.stringify(item.predicted, null, 2)}</pre>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={labelCss}>card.json</div>
        <pre style={preCss}>{cardJsonText}</pre>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={labelCss}>card.lua</div>
        <pre style={preCss}>{item.card_lua}</pre>
      </div>
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <div style={labelCss}>RAW MODEL REPLY</div>
        <pre style={preCss}>{item.raw_reply || "(empty)"}</pre>
        {item.parse_error && (
          <div style={{ color: theme.accent, fontSize: 11 }}>
            parse error: {item.parse_error}
          </div>
        )}
      </div>
    </div>
  );
}
